/**
 * Machine create/edit DrawerPanel form.
 *
 * Provides a slide-out form for creating new machines or editing
 * existing ones. Uses the shared DrawerPanel component with inline
 * validation on submit.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DrawerPanel } from '../../components/shared';
import { useNotification } from '../../providers/NotificationProvider';
import type { DrawerAction } from '../../components/shared';
import { MachineControllerApi, MachineTypeControllerApi } from '../../api-generated/apis';
import type { Machine, MachineType } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const machineApi = new MachineControllerApi(apiConfig);
const machineTypeApi = new MachineTypeControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MachineDrawerFormProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  machineId?: number; // Only for edit mode
}

interface MachineFormData {
  name: string;
  machineTypeId: string;
  description: string;
}

interface FormErrors {
  name?: string;
  machineTypeId?: string;
  description?: string;
}

// ─── Validation ──────────────────────────────────────────────────────────────

function validate(data: MachineFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.length < 1 || data.name.length > 100) {
    errors.name = 'Name must be between 1-100 characters';
  }

  if (!data.machineTypeId) {
    errors.machineTypeId = 'Machine type is required';
  }

  if (data.description && data.description.length > 500) {
    errors.description = 'Description must not exceed 500 characters';
  }

  return errors;
}

// ─── Initial State ───────────────────────────────────────────────────────────

const INITIAL_FORM_DATA: MachineFormData = {
  name: '',
  machineTypeId: '',
  description: '',
};

// ─── Component ───────────────────────────────────────────────────────────────

export const MachineDrawerForm: React.FC<MachineDrawerFormProps> = ({
  open,
  onClose,
  mode,
  machineId,
}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [formData, setFormData] = useState<MachineFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});

  // ─── Fetch machine types for dropdown ──────────────────────────────────
  const machineTypesQuery = useQuery<MachineType[]>({
    queryKey: ['machine-types'],
    queryFn: () => machineTypeApi.getAllMachineTypes(),
  });

  // ─── Fetch machine data in edit mode ───────────────────────────────────
  const machineQuery = useQuery<Machine>({
    queryKey: ['machine', machineId],
    queryFn: () => machineApi.getMachine({ id: String(machineId!) }),
    enabled: open && mode === 'edit' && !!machineId,
  });

  // Populate form when machine data is loaded for editing
  useEffect(() => {
    if (mode === 'edit' && machineQuery.data) {
      setFormData({
        name: machineQuery.data.name ?? '',
        machineTypeId: machineQuery.data.machineType?.id?.toString() ?? '',
        description: machineQuery.data.description ?? '',
      });
      setErrors({});
    }
  }, [mode, machineQuery.data]);

  // Reset form when opening in create mode
  useEffect(() => {
    if (open && mode === 'create') {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
    }
  }, [open, mode]);

  // ─── Mutations ─────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (data: MachineFormData) => {
      return machineApi.createMachine({
        machine: {
          name: data.name,
          machineType: { id: data.machineTypeId },
          description: data.description || undefined,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      onClose();
      notification.showSuccess('Machine created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: MachineFormData) => {
      return machineApi.updateMachine({
        id: String(machineId!),
        machine: {
          name: data.name,
          machineType: { id: data.machineTypeId },
          description: data.description || undefined,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      onClose();
      notification.showSuccess('Machine updated successfully');
    },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleFieldChange = useCallback(
    (field: keyof MachineFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const validationErrors = validate(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (mode === 'create') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate(formData);
    }
  }, [formData, mode, createMutation, updateMutation]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ─── Drawer actions ────────────────────────────────────────────────────

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const actions: DrawerAction[] = [
    {
      label: 'Cancel',
      variant: 'outlined',
      onClick: handleCancel,
      disabled: isSaving,
    },
    {
      label: 'Save',
      variant: 'contained',
      onClick: handleSubmit,
      disabled: isSaving,
      color: 'primary',
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────

  const isLoading = mode === 'edit' && machineQuery.isLoading;

  return (
    <DrawerPanel
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add Machine' : 'Edit Machine'}
      width="narrow"
      actions={actions}
      loading={isLoading}
    >
      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
      >
        <TextField
          label="Name"
          value={formData.name}
          onChange={handleFieldChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          required
          fullWidth
          inputProps={{ maxLength: 100 }}
        />

        <TextField
          label="Machine Type"
          value={formData.machineTypeId}
          onChange={handleFieldChange('machineTypeId')}
          error={!!errors.machineTypeId}
          helperText={errors.machineTypeId}
          required
          fullWidth
          select
          disabled={machineTypesQuery.isLoading}
        >
          {(machineTypesQuery.data ?? []).map((type: MachineType) => (
            <MenuItem key={type.id} value={type.id?.toString() ?? ''}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Description"
          value={formData.description}
          onChange={handleFieldChange('description')}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          multiline
          rows={4}
          inputProps={{ maxLength: 500 }}
        />
      </Box>
    </DrawerPanel>
  );
};

export default MachineDrawerForm;
